import React, { useState } from "react";
import EventsService from "../services/EventsService";
import "./TimepointCalendar.css";

export const TimepointCalendar = ({ timeArray, actions, onTimepointChanged }) => {
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editTime, setEditTime] = useState("");
  const [editNote, setEditNote] = useState("");
  const [loading, setLoading] = useState(false);

  const openModal = (tp) => {
    setSelected(tp);
    setEditing(false);
    const d = new Date(tp.time);
    setEditTime(formatDatetimeLocal(d));
    setEditNote(tp.note || "");
  };

  const formatDatetimeLocal = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  const handleDelete = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      await EventsService.deleteTimepoint(selected.id);
      setSelected(null);
      if (onTimepointChanged) onTimepointChanged();
    } catch (e) {
      alert("Failed to delete: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const newTime = Math.floor(new Date(editTime).getTime() / 1000);
      await EventsService.editTimepoint(selected.id, {
        time: newTime,
        note: editNote || undefined,
      });
      setSelected(null);
      setEditing(false);
      if (onTimepointChanged) onTimepointChanged();
    } catch (e) {
      alert("Failed to save: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const sorted = [...timeArray].sort((a, b) => new Date(b.time) - new Date(a.time));

  const getDateKey = (time) => {
    const d = new Date(time);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  };

  const formatDateLabel = (time) => {
    const d = new Date(time);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (getDateKey(time) === getDateKey(today)) return "Today";
    if (getDateKey(time) === getDateKey(yesterday)) return "Yesterday";
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  };

  const items = [];
  let lastDateKey = null;
  for (const tp of sorted) {
    const dk = getDateKey(tp.time);
    if (dk !== lastDateKey) {
      items.push({ type: "date", dateLabel: formatDateLabel(tp.time), key: `date-${dk}` });
      lastDateKey = dk;
    }
    items.push({ type: "tp", data: tp, key: tp.id || `tp-${items.length}` });
  }

  return (
    <div className="timepoint-calendar">
      {items.length > 0 ? (
        <ul>
          {items.map((item) => {
            if (item.type === "date") {
              return (
                <li key={item.key} className="tp-date-separator">
                  <span>{item.dateLabel}</span>
                </li>
              );
            }
            const timepoint = item.data;
            const action = actions.find((a) => a.id === timepoint.actionId);
            return (
              <li
                key={item.key}
                style={{ backgroundColor: action?.color || "", cursor: "pointer" }}
                onClick={() => openModal(timepoint)}
              >
                <span>{action ? action.name : "Unknown"}</span>
                <span>{new Date(timepoint.time).toLocaleTimeString()}</span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p style={{ color: "rgba(255,255,255,0.4)" }}>No timepoints recorded yet.</p>
      )}

      {selected && (
        <div className="tp-modal-overlay" onClick={() => { setSelected(null); setEditing(false); }}>
          <div className="tp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tp-modal-header">
              <h3>
                {(() => {
                  const action = actions.find((a) => a.id === selected.actionId);
                  return action ? action.name : "Unknown";
                })()}
              </h3>
              <button className="tp-modal-close" onClick={() => { setSelected(null); setEditing(false); }}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            {!editing ? (
              <div className="tp-modal-body">
                <div className="tp-modal-field">
                  <label>Date & Time</label>
                  <span>{new Date(selected.time).toLocaleString()}</span>
                </div>
                {selected.note && (
                  <div className="tp-modal-field">
                    <label>Note</label>
                    <span>{selected.note}</span>
                  </div>
                )}
                <div className="tp-modal-actions">
                  <button className="tp-modal-btn tp-modal-btn-edit" onClick={() => setEditing(true)} disabled={loading}>
                    <i className="fas fa-pen"></i> Edit
                  </button>
                  <button className="tp-modal-btn tp-modal-btn-delete" onClick={handleDelete} disabled={loading}>
                    <i className="fas fa-trash"></i> Delete
                  </button>
                </div>
              </div>
            ) : (
              <div className="tp-modal-body">
                <div className="tp-modal-field">
                  <label>Date & Time</label>
                  <input
                    type="datetime-local"
                    step="1"
                    value={editTime}
                    onChange={(e) => setEditTime(e.target.value)}
                    className="tp-modal-input"
                  />
                </div>
                <div className="tp-modal-field">
                  <label>Note</label>
                  <input
                    type="text"
                    value={editNote}
                    onChange={(e) => setEditNote(e.target.value)}
                    placeholder="Optional note..."
                    className="tp-modal-input"
                  />
                </div>
                <div className="tp-modal-actions">
                  <button className="tp-modal-btn tp-modal-btn-save" onClick={handleSave} disabled={loading}>
                    <i className="fas fa-check"></i> Save
                  </button>
                  <button className="tp-modal-btn tp-modal-btn-cancel" onClick={() => setEditing(false)} disabled={loading}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
